import { create } from 'zustand'

/** A fast tap-tap chord shouldn't flash a broadcast hint it completes before anyone could read it —
 *  only a pause this long earns the sidebar lighting up with every reachable item's next key. */
const HINT_APPEAR_DELAY_MS = 200

interface SequenceHintState {
	/** Lowercase starter key currently armed and waiting on its second key, or null. */
	armedKey: string | null
	/** Same as `armedKey`, but delayed on the way in by `HINT_APPEAR_DELAY_MS` — what the sidebar's
	 *  broadcast hints actually key off of. Clears the instant `armedKey` does: moving off the starter
	 *  key (or any other cancel) must hide an already-visible hint immediately, never on a timer. */
	visibleArmedKey: string | null
	setArmedKey: (key: string | null) => void
}

let appearTimer: ReturnType<typeof setTimeout> | null = null
const clearAppearTimer = () => {
	if (appearTimer !== null) {
		clearTimeout(appearTimer)
		appearTimer = null
	}
}

/** Bridges the sequence engine's armed/pending state (which has no view of the UI) to anything that
 *  wants to show a live hint while a chord is waiting on its second key, e.g. the sidebar nav. */
export const useSequenceHintStore = create<SequenceHintState>()((set, get) => ({
	armedKey: null,
	visibleArmedKey: null,
	setArmedKey: key => {
		clearAppearTimer()
		set({ armedKey: key })
		if (key === null) {
			set({ visibleArmedKey: null })
			return
		}
		appearTimer = setTimeout(() => {
			appearTimer = null
			// Still the same key armed, not a different starter pressed mid-delay or the window expired.
			if (get().armedKey === key) {
				set({ visibleArmedKey: key })
			}
		}, HINT_APPEAR_DELAY_MS)
	},
}))
